import React, { useState, useEffect, useRef } from 'react';
import { Box, makeStyles, Typography, Button } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import { Page, Content } from '@backstage/core-components';
import {
  useApi,
  identityApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';
import { Android as AndroidIcon } from '@material-ui/icons';
import { Message, Agent } from './types';
import { getAgentConfig } from './utils';
import { ChatInput } from './ChatInput';
import { Sidebar } from './Sidebar';
import { ChatHeader } from './ChatHeader';
import { LoadingState } from './LoadingState';
import { MessagesContainer } from './MessagesContainer';
import { PromptContainer } from './PromptContainer';
import { AgentService } from './services';
import { useMessageHandler } from './hooks';
import { Breadcrumbs } from '@internal/backstage-plugin-global-header';

const useStyles = makeStyles(theme => ({
  pageRoot: {
    height: '100% !important',
    '& > article': {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    },
    '& > article > *:first-child': {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
    },
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    padding: theme.spacing(2, 3, 0),
  },
  mainContainer: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
  mainContainerSts: {
    background: 'transparent !important',
    padding: '0 !important',
    height: 'calc(100vh - 140px)',
    display: 'flex',
    flexDirection: 'column',
  },
  chatSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  chatContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    border: `1px solid ${theme.palette.grey[200]}`,
    overflow: 'hidden',
    backdropFilter: 'blur(10px)',
    transition: 'border-color 0.3s ease',
  },
  chatContainerSts: {
    borderRadius: '0 !important',
    boxShadow: 'none !important',
    border: 'none !important',
    backgroundColor: 'transparent !important',
    position: 'relative' as any,
    flex: 1,
    minHeight: 0,
  },
  inputArea: {
    padding: theme.spacing(1.5, 1.8),
    background: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.grey[100]}`,
  },
  inputAreaSts: {
    borderTop: 'none !important',
    backgroundColor: 'transparent !important',
    padding: '0 !important',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    background:
      'linear-gradient(to top, rgba(245,245,245,0.98) 70%, rgba(245,245,245,0)) !important',
    paddingBottom: '12px !important',
    paddingTop: '48px !important',
  },
}));

interface AgentChatComponentProps {
  agent?: string;
  namespace?: string;
  backPath?: string;
  basePath?: string;
  showAgentsSidebar?: boolean;
  stsMode?: boolean;
  onSendMessage?: (sendFn: (message: string, overrideSessionId?: string) => void) => void;
  onMessageSent?: () => void;
  extraContext?: Record<string, string>;
  sessionId?: string;
  showSaveButton?: boolean;
  saveTicketKey?: string;
  ticketSelected?: boolean;
  initialMessage?: string;
  defaultInputValue?: string;
  onSaveMessage?: () => void;
  isSaveLoading?: boolean;
}

export const AgentChatComponent = ({
  agent: agentProp,
  namespace,
  basePath = '/marketplace/home/agent-chat',
  showAgentsSidebar = true,
  stsMode = false,
  onSendMessage,
  onMessageSent,
  extraContext,
  sessionId: sessionIdProp,
  showSaveButton = false,
  saveTicketKey,
  ticketSelected = true,
  initialMessage,
  defaultInputValue,
  onSaveMessage,
  isSaveLoading = false,
}: AgentChatComponentProps = {}) => {
  const classes = useStyles();
  const { agentApiName: agentApiNameParam } = useParams<{
    agentApiName?: string;
  }>();
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const baseUrl = configApi.getString('backend.baseUrl');
  const agentFromUrl = (agentProp || agentApiNameParam)?.replace(/-/g, '_');

  const [agentMessages, setAgentMessages] = useState<Record<string, Message[]>>({});
  const [typingAgents, setTypingAgents] = useState<Record<string, boolean>>({});
  const [selectedAgent, setSelectedAgent] = useState<string>(agentFromUrl || '');
  const [agents, setAgents] = useState<Agent[]>([]);
  const sessionIdsRef = useRef<Record<string, string>>({});
  const setSessionIdsSync = (updater: (prev: Record<string, string>) => Record<string, string>) => {
    sessionIdsRef.current = updater(sessionIdsRef.current);
  };
  const [inputValue, setInputValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sidebarTabValue, setSidebarTabValue] = useState<number>(0);
  const [showPrompts, setShowPrompts] = useState<boolean>(true);

  const messages = agentMessages[selectedAgent] || [];
  const agentService = new AgentService(baseUrl, identityApi);

  const { handleSendMessage, abortCurrentStream } = useMessageHandler(
    agentService,
    agents,
    selectedAgent,
    sessionIdsRef.current,
    agentMessages,
    setAgentMessages,
    setTypingAgents,
    extraContext,
    onMessageSent,
  );

  const fetchApps = async (targetAgentFromUrl?: string) => {
    try {
      setIsLoading(true);
      const allAgents = await agentService.fetchAgents(namespace);
      setAgents(allAgents);

      let targetAgent = targetAgentFromUrl;
      if (!targetAgent && allAgents.length > 0) {
        targetAgent = allAgents[0].apiName || allAgents[0].name;
      }

      if (targetAgent) {
        const agent = allAgents.find(
          a =>
            a.apiName === targetAgent || a.apiName?.endsWith(`_${targetAgent}`),
        );
        if (agent) {
          setSelectedAgent(agent.name);
          setShowPrompts(true);

          let resolvedSessionId: string | undefined;

          if (sessionIdProp) {
            setSessionIdsSync(prev => ({
              ...prev,
              [agent.apiName || agent.name]: sessionIdProp,
            }));
            resolvedSessionId = sessionIdProp;
          } else if (agent.protocol !== 'json') {
            const sessionId = await agentService.createSession(
              agent.apiName || agent.name,
            );
            if (sessionId) {
              setSessionIdsSync(prev => ({
                ...prev,
                [agent.apiName || agent.name]: sessionId,
              }));
              resolvedSessionId = sessionId;
            }
          }

          if (!stsMode) {
            setAgentMessages({
              [agent.name]: [
                {
                  id: 1,
                  type: 'agent',
                  content: `Hello! I'm ${agent.heading}. How can I assist you today?`,
                  timestamp: new Date(),
                  agentType: agent.name,
                  status: 'delivered',
                },
              ],
            });
          } else {
            setAgentMessages({ [agent.name]: [] });
          }

          if (initialMessage) {
            const agentApiName = agent.apiName || agent.name;
            setTimeout(async () => {
              const userMsg: Message = {
                id: 1,
                type: 'user',
                content: initialMessage,
                timestamp: new Date(),
                status: 'sent',
              };
              setAgentMessages(prev => ({ ...prev, [agent.name]: [...(prev[agent.name] || []), userMsg] }));
              setTypingAgents(prev => ({ ...prev, [agent.name]: true }));
              try {
                const response = await agentService.sendMessage(initialMessage, agentApiName, resolvedSessionId, new AbortController().signal, extraContext);
                if (response.ok) {
                  const reader = response.body?.getReader();
                  const decoder = new TextDecoder();
                  if (reader) {
                    let buffer = '';
                    while (true) {
                      const { done, value } = await reader.read();
                      if (done) break;
                      buffer += decoder.decode(value, { stream: true });
                      const lines = buffer.split('\n');
                      buffer = lines.pop() || '';
                      for (const line of lines) {
                        if (!line.startsWith('data: ')) continue;
                        try {
                          const data = JSON.parse(line.substring(6));
                          if (data.event_type === 'final_message') {
                            setAgentMessages(prev => {
                              const msgs = prev[agent.name] || [];
                              return { ...prev, [agent.name]: [...msgs, { id: msgs.length + 1, type: 'agent', content: data.content || 'Response received', timestamp: new Date(), agentType: agent.name, status: 'delivered' }] };
                            });
                          }
                        } catch { /* skip */ }
                      }
                    }
                    reader.releaseLock();
                  }
                }
              } catch (err) {
                console.error('[AgentChat] initialMessage send error:', err);
              } finally {
                setTypingAgents(prev => ({ ...prev, [agent.name]: false }));
              }
            }, 300);
          }
        }
      }
    } catch (err) {
      setError('Failed to load agents. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (defaultInputValue) setInputValue(defaultInputValue);
  }, [defaultInputValue]);

  useEffect(() => {
    if (!sessionIdProp || !selectedAgent) return;
    const agentObj = agents.find(a => a.name === selectedAgent);
    const agentApiName = agentObj?.apiName || selectedAgent;
    setSessionIdsSync(prev => ({ ...prev, [agentApiName]: sessionIdProp }));
  }, [sessionIdProp]);

  useEffect(() => {
    fetchApps(agentFromUrl);
  }, [identityApi, baseUrl, agentFromUrl]);

  React.useEffect(() => {
    if (onSendMessage) {
      onSendMessage(handleSendMessage);
    }
  }, [onSendMessage, handleSendMessage]);

  const currentAgentConfig = getAgentConfig(selectedAgent, agents) ?? {
    name: selectedAgent,
    icon: 'bot',
    color: '#1976d2',
  };

  const handleAgentSelect = async (agentName: string): Promise<void> => {
    setSelectedAgent(agentName);
    setShowPrompts(true);

    if (!agentMessages[agentName]) {
      const selectedAgentObj = agents.find(a => a.name === agentName);
      const agentApiName = selectedAgentObj?.apiName || agentName;
      const agentHeading = selectedAgentObj?.heading || agentName;

      if (selectedAgentObj?.protocol !== 'json') {
        const sessionId = await agentService.createSession(agentApiName);
        if (sessionId) {
          setSessionIdsSync(prev => ({ ...prev, [agentApiName]: sessionId }));
        }
      }

      if (!stsMode) {
        setAgentMessages(prev => ({
          ...prev,
          [agentName]: [
            {
              id: 1,
              type: 'agent',
              content: `Hello! I'm ${agentHeading}. How can I assist you today?`,
              timestamp: new Date(),
              agentType: agentName,
              status: 'delivered',
            },
          ],
        }));
      } else {
        setAgentMessages(prev => ({ ...prev, [agentName]: [] }));
      }
    }
  };

  const handleRefreshSession = async (): Promise<void> => {
    setIsRefreshing(true);
    abortCurrentStream();

    const agentApiName = currentAgentConfig.apiName || currentAgentConfig.name;
    const agentHeading = currentAgentConfig.heading || selectedAgent;

    if (currentAgentConfig.protocol !== 'json') {
      const sessionId = await agentService.createSession(agentApiName);
      if (sessionId) {
        setSessionIdsSync(prev => ({ ...prev, [agentApiName]: sessionId }));
      }
    }

    if (!stsMode) {
      setAgentMessages(prev => ({
        ...prev,
        [selectedAgent]: [
          {
            id: 1,
            type: 'agent',
            content: `Hello! I'm ${agentHeading}. How can I assist you today?`,
            timestamp: new Date(),
            agentType: selectedAgent,
            status: 'delivered',
          },
        ],
      }));
    } else {
      setAgentMessages(prev => ({ ...prev, [selectedAgent]: [] }));
    }

    setShowPrompts(true);
    setIsRefreshing(false);
  };

  if (stsMode) {
    if (error) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" style={{ height: '100%' }}>
          <Box textAlign="center">
            <Typography variant="h6" color="error" gutterBottom>{error}</Typography>
            <Button variant="outlined" color="primary" onClick={() => fetchApps(agentFromUrl)}>Retry</Button>
          </Box>
        </Box>
      );
    }

    if (isLoading) {
      return (
        <Box style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
          <Box style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: 16, marginBottom: 24 }}>
            <Box style={{ width: 220, height: 22, borderRadius: 4, background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
            <Box style={{ width: 140, height: 14, borderRadius: 4, marginTop: 8, background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
          </Box>
          <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
            {[{ w: '45%', align: 'flex-start', h: 48 }, { w: '35%', align: 'flex-end', h: 36 }, { w: '60%', align: 'flex-start', h: 64 }, { w: '30%', align: 'flex-end', h: 36 }, { w: '50%', align: 'flex-start', h: 52 }].map((item, i) => (
              <Box key={i} style={{ display: 'flex', justifyContent: item.align }}>
                <Box style={{ width: item.w, height: item.h, borderRadius: 12, background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', animation: `shimmer 1.4s ${i * 0.15}s infinite` }} />
              </Box>
            ))}
          </Box>
          <Box style={{ display: 'flex', justifyContent: 'center', paddingTop: 16, paddingBottom: 8 }}>
            <Box style={{ width: '60%', height: 38, borderRadius: 32, background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s 0.6s infinite' }} />
          </Box>
          <style>{`@keyframes shimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }`}</style>
        </Box>
      );
    }

    if (agents.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" style={{ height: '100%' }}>
          <Box style={{ padding: 32, textAlign: 'center', maxWidth: 500, borderRadius: 4, boxShadow: '0 2px 4px rgba(0,0,0,0.08)', backgroundColor: '#ffffff', border: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <AndroidIcon style={{ fontSize: 80, color: '#1976d2', marginBottom: 8 }} />
            <Typography variant="h4" gutterBottom>No Agents Registered</Typography>
            <Typography variant="body1" color="textSecondary">No agents have been registered yet.</Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box className={classes.chatSection}>
        <Box className={`${classes.chatContainer} ${classes.chatContainerSts}`}>
          <ChatHeader
            agentConfig={currentAgentConfig}
            selectedAgent={selectedAgent}
            onRefresh={handleRefreshSession}
            isRefreshing={isRefreshing}
            basePath={basePath}
            stsMode={stsMode}
          />
          <MessagesContainer
            messages={messages}
            agents={agents}
            isTyping={typingAgents[selectedAgent] || false}
            agentConfig={currentAgentConfig}
            stsMode={stsMode}
            onSendMessage={handleSendMessage}
          />
          {currentAgentConfig.prompts &&
            Object.keys(currentAgentConfig.prompts).length > 0 &&
            messages.length === 0 &&
            showPrompts && (
              <PromptContainer
                prompts={currentAgentConfig.prompts}
                onPromptSelect={setInputValue}
                onViewMore={() => setSidebarTabValue(1)}
                onClose={() => setShowPrompts(false)}
                selectedTabValue={sidebarTabValue}
                stsMode={stsMode}
              />
            )}
          <Box className={`${classes.inputArea} ${classes.inputAreaSts}`}>
            <ChatInput
              onSend={handleSendMessage}
              placeholder={`Chat with ${selectedAgent} ...`}
              value={inputValue}
              onValueChange={setInputValue}
              disabled={!ticketSelected}
              isTyping={typingAgents[selectedAgent]}
              stsMode={stsMode}
              showSaveButton={showSaveButton}
              saveTicketKey={saveTicketKey}
              onSave={onSaveMessage}
              isSaving={isSaveLoading}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Page themeId="tool" className={classes.pageRoot}>
        <Content noPadding>
          <Box className={classes.mainContainer} display="flex" alignItems="center" justifyContent="center">
            <Box textAlign="center">
              <Typography variant="h6" color="error" gutterBottom>{error}</Typography>
              <Button variant="outlined" color="primary" onClick={() => fetchApps(agentFromUrl)}>Retry</Button>
            </Box>
          </Box>
        </Content>
      </Page>
    );
  }

  if (isLoading) {
    return (
      <Page themeId="tool" className={classes.pageRoot}>
        <Content noPadding>
          <Box className={classes.mainContainer}>
            <Box className={classes.chatSection}>
              <Box className={classes.chatContainer}>
                <LoadingState />
              </Box>
            </Box>
          </Box>
        </Content>
      </Page>
    );
  }

  if (agents.length === 0) {
    return (
      <Page themeId="tool" className={classes.pageRoot}>
        <Content noPadding>
          <Box display="flex" justifyContent="center" alignItems="center" className={classes.mainContainer}>
            <Box style={{ padding: 32, textAlign: 'center', maxWidth: 500, borderRadius: 16, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', backgroundColor: '#ffffff', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <AndroidIcon style={{ fontSize: 80, color: '#1976d2', marginBottom: 8 }} />
              <Typography variant="h4" gutterBottom>No Agents Registered</Typography>
              <Typography variant="body1" color="textSecondary">No agents have been registered yet.</Typography>
            </Box>
          </Box>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool" className={classes.pageRoot}>
      <Content noPadding>
        <Box className={classes.contentWrapper}>
          <Breadcrumbs disableMargin />
          <Box className={classes.mainContainer}>
            <Box className={classes.chatSection}>
              <Box className={classes.chatContainer}>
                <ChatHeader
                  agentConfig={currentAgentConfig}
                  selectedAgent={selectedAgent}
                  onRefresh={handleRefreshSession}
                  isRefreshing={isRefreshing}
                  basePath={basePath}
                  stsMode={stsMode}
                />
                <MessagesContainer
                  messages={messages}
                  agents={agents}
                  isTyping={typingAgents[selectedAgent] || false}
                  agentConfig={currentAgentConfig}
                  stsMode={stsMode}
                  onSendMessage={handleSendMessage}
                />
                {currentAgentConfig.prompts &&
                  Object.keys(currentAgentConfig.prompts).length > 0 &&
                  messages.length === 0 &&
                  showPrompts && (
                    <PromptContainer
                      prompts={currentAgentConfig.prompts}
                      onPromptSelect={setInputValue}
                      onViewMore={() => setSidebarTabValue(1)}
                      onClose={() => setShowPrompts(false)}
                      selectedTabValue={sidebarTabValue}
                      stsMode={stsMode}
                    />
                  )}
                <Box className={classes.inputArea}>
                  <ChatInput
                    onSend={handleSendMessage}
                    placeholder={`Chat with ${selectedAgent} ...`}
                    value={inputValue}
                    onValueChange={setInputValue}
                    disabled={typingAgents[selectedAgent] || false}
                    stsMode={stsMode}
                  />
                </Box>
              </Box>
            </Box>

            {showAgentsSidebar && (
              <Sidebar
                agents={agents}
                selectedAgent={selectedAgent}
                onAgentSelect={handleAgentSelect}
                messages={messages}
                onPromptSelect={setInputValue}
                tabValue={sidebarTabValue}
                basePath={basePath}
                onTabChange={value => setSidebarTabValue(value)}
              />
            )}
          </Box>
        </Box>
      </Content>
    </Page>
  );
};

export default AgentChatComponent;
